import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../../../utils/apiClient';
import type {
  SelectionMode,
  SelectionSignalReadiness,
  SignalOverride,
} from '../utils/selectionFormatting';
import { normalizeSelectionReadiness } from '../utils/selectionFormatting';

export const selectionReadinessRequestKey = ({
  projectId,
  mode,
  signalOverride,
}: {
  projectId?: string;
  mode: SelectionMode;
  signalOverride: SignalOverride;
}) => ['selection-readiness', mode, projectId || '', signalOverride] as const;

export const selectionReadinessEndpoint = ({
  projectId,
  mode,
  signalOverride,
}: {
  projectId: string;
  mode: SelectionMode;
  signalOverride: SignalOverride;
}) =>
  `/${mode}/signal-readiness?project_id=${encodeURIComponent(
    projectId
  )}&signal_override=${encodeURIComponent(signalOverride)}`;

export const useSelectionReadiness = ({
  projectId,
  mode,
  signalOverride,
}: {
  projectId?: string;
  mode: SelectionMode;
  signalOverride: SignalOverride;
}) => {
  const [signalReadiness, setSignalReadiness] =
    useState<SelectionSignalReadiness | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const requestKey = useMemo(
    () =>
      selectionReadinessRequestKey({
        projectId,
        mode,
        signalOverride,
      }).join('|'),
    [mode, projectId, signalOverride]
  );
  const requestSequenceRef = useRef(0);
  const latestRequestKeyRef = useRef(requestKey);
  latestRequestKeyRef.current = requestKey;

  const loadSignalReadiness = useCallback(async () => {
    if (!projectId) {
      setSignalReadiness(null);
      setLoading(false);
      return;
    }

    const sequence = ++requestSequenceRef.current;
    const requestedProjectId = projectId;
    const requestedKey = requestKey;
    setLoading(true);
    setError(null);

    try {
      const readiness = await api.get<SelectionSignalReadiness>(
        selectionReadinessEndpoint({
          projectId: requestedProjectId,
          mode,
          signalOverride,
        })
      );
      if (latestRequestKeyRef.current !== requestedKey || requestSequenceRef.current !== sequence) {
        return;
      }
      if (readiness.project_id && readiness.project_id !== requestedProjectId) {
        return;
      }
      setSignalReadiness(normalizeSelectionReadiness(readiness));
    } catch (err) {
      if (latestRequestKeyRef.current !== requestedKey || requestSequenceRef.current !== sequence) {
        return;
      }
      setSignalReadiness(null);
      setError(err);
    } finally {
      if (latestRequestKeyRef.current === requestedKey && requestSequenceRef.current === sequence) {
        setLoading(false);
      }
    }
  }, [mode, projectId, requestKey, signalOverride]);

  useEffect(() => {
    setSignalReadiness(null);
    setError(null);
    void loadSignalReadiness();
  }, [loadSignalReadiness]);

  return {
    signalReadiness,
    setSignalReadiness,
    loadSignalReadiness,
    loading,
    error,
  };
};
